import { Spinner } from "components/DesignSystem/Loader/loader";
import { ENTITY_CREATION_TIMESTAMP } from "constants/storageKeys";
import { useRedirectOnAuth } from "hooks/useRedirectOnAuth";
import { useEffect, useState } from "react";
import { useLazyGetUserProfileQuery } from "store/apis/profile";
import { SignupResumeRedirect } from "v2/components/onboarding/SignupResumeRedirect";
import { useSignupResumeState } from "v2/utils/useSignupResumeState";
import { VerifyFailedScreen } from "./VerifyingUI/VerifyFailedScreen";
import { VerifySetupSteps } from "./VerifyingUI/VerifySetupSteps";
import { VerifySuccessScreen } from "./VerifyingUI/VerifySuccessScreen";
import { PHASE } from "./constants";

type Phase = (typeof PHASE)[keyof typeof PHASE];

const SCREEN1_DURATION_MS = 4000;
const POLL_INTERVAL_MS = 3000;
const MAX_SCREEN2_DURATION_MS = 2 * 60 * 1000;

const getInitialPhase = (): Phase => {
  const stored = localStorage.getItem(ENTITY_CREATION_TIMESTAMP);
  if (!stored) return PHASE.FAILED;

  const elapsed = Date.now() - Number(stored);
  return elapsed < MAX_SCREEN2_DURATION_MS ? PHASE.SCREEN1 : PHASE.FAILED;
};

export const Verifying = () => {
  const [phase, setPhase] = useState<Phase>(getInitialPhase);
  const [getProfile] = useLazyGetUserProfileQuery();
  const { isSignupPending, isChecking } = useSignupResumeState();
  useRedirectOnAuth({ successDelay: 4000 });

  useEffect(() => {
    const mountTime = Date.now();
    let pollTimer: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const screen1Timer = setTimeout(
      () =>
        setPhase((current) =>
          current === PHASE.SCREEN1 ? PHASE.SCREEN2 : current
        ),
      SCREEN1_DURATION_MS
    );

    const pollProfile = async () => {
      if (cancelled) return;

      const elapsed = Date.now() - mountTime;
      if (elapsed >= SCREEN1_DURATION_MS + MAX_SCREEN2_DURATION_MS) {
        setPhase(PHASE.FAILED);
      }

      try {
        const profile = await getProfile().unwrap();
        if (cancelled) return;

        if (!profile?.is_restricted && profile?.is_group_created) {
          setPhase(PHASE.SUCCESS);
          return;
        }
      } catch (error) {}

      if (!cancelled) {
        pollTimer = setTimeout(pollProfile, POLL_INTERVAL_MS);
      }
    };

    pollProfile();

    return () => {
      cancelled = true;
      clearTimeout(screen1Timer);
      clearTimeout(pollTimer);
    };
  }, []);

  const renderScreen = () => {
    if (isChecking || isSignupPending) {
      return (
        <div className="t-flex t-h-screen t-w-full t-items-center t-justify-center">
          <Spinner />
        </div>
      );
    }

    if (phase === PHASE.SUCCESS) return <VerifySuccessScreen />;
    if (phase === PHASE.FAILED) return <VerifyFailedScreen />;

    return <VerifySetupSteps phase={phase} />;
  };

  return (
    <>
      <SignupResumeRedirect />
      {renderScreen()}
    </>
  );
};
